import React from 'react';
import EmptySourceField from '../ui/EmptySourceField';

/**
 * CustomerTypeCell — the D-17 "Customer type" row of the WO assignment card.
 *
 * PR-72 Commit 4 (D-17, stakeholder UAT 08-19 :125, register R-107).
 *
 * Extracted from WorkOrderDetail.js's inline row (lines 1962-1963 in the
 * pre-PR-72 tree) so the state-resolution ladder is testable in isolation
 * and cannot drift from the assertions.
 *
 * ── Props ──────────────────────────────────────────────────────────────
 *
 *   settings  — the useSettingsContext value (or a compatible shape in
 *               tests). Read directly rather than calling
 *               useSettingsContext() ourselves so callers can pass a
 *               mock without wrapping in a provider.
 *   customer  — the hydrated customer record from api.getCustomer, or
 *               null if hydration is in flight / has not run.
 *   wo        — the work order record. Currently only used to defend
 *               against callers that used to render wo.customerName
 *               (no longer valid post-D-17; kept in the signature so
 *               ledger-migrated call sites don't silently drop it).
 *
 * ── Resolution ladder (dispatch 415 §2 + 416 §3) ───────────────────────
 *
 *   customer == null                            → em-dash
 *   settings.loading                            → skeleton
 *   getError(customer_types)                    → em-dash + Family-C notice
 *   Array.isArray(items) && items.length === 0  → <EmptySourceField>
 *   items == null (no error, no data)           → em-dash (unclassified)
 *   resolved via getEnumLabel                   → the label
 *   value absent, list populated                → em-dash
 *
 * ── The invariant: ERROR and EMPTY are DISJOINT (dispatch 417 §2) ─────
 *
 * The real useSettings loader (:145-152) writes newErrors OR newCache,
 * never both — an ERROR leaves the cache KEY ABSENT, so ERROR's
 * observable is `getError(lt) != null`; EMPTY's observable is the
 * NARROWED `Array.isArray(items) && items.length === 0`. These two
 * predicates cannot both be true for the same state, so any state
 * matches at most one — the ordering below is defensive, not load-
 * bearing.
 *
 * That disjointness is what makes the ladder safe, not the sequence.
 * The braces, not the belt. A future edit that keeps the ordering but
 * widens EMPTY back to `!items` (the pre-e3090ee shape) breaks the
 * disjointness — ERROR and MISSING both become "empty" — and reveals
 * why the mutation dispatch 416 flagged (bare `!items`) was Family C:
 * a network failure would fall into EmptySourceField's "Go to Settings"
 * CTA.
 *
 * The rule to preserve, therefore:
 *     ERROR-observable   and   EMPTY-observable   must never
 *     both match the same production state.
 * If you touch either predicate, re-derive both against
 * useSettings:145-152 (or its successor) before shipping.
 *
 * ── EMPTY before RESOLUTION (dispatch 415 §2 correction 1) ────────────
 *
 * Separate concern, still load-bearing: step 4 of getEnumLabel
 * (prettifyEnum) manufactures a plausible label from any raw key. If we
 * resolved before checking EMPTY, an unconfigured tenant would see
 * "Commercial" for customer_type: 'commercial' and believe the field
 * works. See useResourceCategories.js docblock for the dormant
 * plurality-hazard variant of the same shape.
 *
 * The unclassified fifth row (items == null && !error) is a state the
 * loader shouldn't emit; we defend against it explicitly rather than
 * letting it fall through to prettifyEnum. Em-dash by design.
 *
 * ── Legacy key alias (dispatch 415 §2) ─────────────────────────────────
 *
 * Canonical: customer.customer_type. Legacy: customer.type. Two existing
 * readers (AddCustomerDrawer:97, CustomerFormPanel:131) defend
 * `customer_type || type`. This reader does the same.
 */
export function CustomerTypeCell({ settings, customer, wo }) {
  // wo is intentionally unread here — see docblock. Kept in the signature
  // so downstream callers don't silently drop context.
  void wo;

  const label = (
    <dt className="text-xs font-medium text-gray-500 uppercase">Customer type</dt>
  );

  // 1) Customer not hydrated yet → em-dash. Preserves the pre-D-17
  //    display shape for the loading-customer state.
  if (customer == null) {
    return (
      <>
        {label}
        <dd className="text-sm text-gray-900 mt-0.5">—</dd>
      </>
    );
  }

  // 2) Settings still loading → skeleton on that cell only.
  if (settings?.loading) {
    return (
      <>
        {label}
        <dd
          className="mt-0.5 h-4 w-24 animate-pulse rounded bg-gray-200"
          data-testid="customer-type-skeleton"
          aria-label="Loading customer type"
        />
      </>
    );
  }

  // 3) ERROR — the fetch failed. Observable is `getError(lt) != null`;
  //    disjoint from EMPTY's `Array.isArray && length === 0` (see
  //    docblock: dispatch 417 §2). Order here is defensive, not
  //    load-bearing — do not widen the EMPTY predicate below or the
  //    two branches stop being disjoint and this ordering becomes a
  //    tripwire instead of a comment.
  const err = settings?.getError && settings.getError('customer_types');
  if (err) {
    return (
      <>
        {label}
        <dd className="text-sm text-gray-900 mt-0.5">
          —
          <span
            data-testid="customer-type-error-notice"
            className="ml-2 text-xs text-amber-700"
          >
            Customer types couldn't be loaded.
          </span>
        </dd>
      </>
    );
  }

  // 4) EMPTY — the tenant has an EXPLICITLY empty configured list.
  //    Narrowed to `Array.isArray && length === 0` (dispatch 416 §3).
  //    A missing key without an error is classified separately in step
  //    5 as em-dash — do not let it flow here.
  const tenantItems = settings?.cache?.customer_types;
  if (Array.isArray(tenantItems) && tenantItems.length === 0) {
    return (
      <>
        {label}
        <dd className="mt-0.5">
          <EmptySourceField
            fieldLabel={null /* label is already rendered by <dt> above */}
            triggerText="Set up types"
            // COPY: PROVISIONAL. Drafted by Portica; no stakeholder wording exists
            // for E-41 at field scale (register :113/:235 gives the ruling shape,
            // not the words). OWNER: Adrian. Provisional until PORTICA-86 closes.
            // Do not treat as ruled copy and do not propagate this wording to a
            // second consumer while this ticket is open (§8.11). If a second
            // EmptySourceField consumer appears (sub-area case in the quote
            // builder is the next likely site — the original E-41 scope), it
            // writes its own provisional copy and flags it the same way. Two
            // flagged provisionals are honest; one unflagged copy of a
            // provisional is how a draft becomes the house standard.
            modalTitle="No customer types set up yet"
            modalBody="Customer types come from your settings. Add at least one and it will be available here and everywhere else customers are categorised."
            ctaLabel="Go to Settings → Customer Types"
            ctaHref="/settings/customer-types"
          />
        </dd>
      </>
    );
  }

  // 5) Unclassified: missing cache key AND no reported error. Defensive
  //    — the loader shouldn't emit this, but if it does we must not let
  //    it fall through to resolution (prettifyEnum would manufacture a
  //    plausible label). Em-dash.
  if (tenantItems == null) {
    return (
      <>
        {label}
        <dd className="text-sm text-gray-900 mt-0.5">—</dd>
      </>
    );
  }

  // 6) Resolution — read canonical key with legacy fallback.
  const value = customer.customer_type || customer.type || '';
  if (!value) {
    return (
      <>
        {label}
        <dd className="text-sm text-gray-900 mt-0.5">—</dd>
      </>
    );
  }

  const resolved = settings?.getEnumLabel
    ? settings.getEnumLabel('customer_types', value)
    : value;

  return (
    <>
      {label}
      <dd className="text-sm text-gray-900 mt-0.5">{resolved || '—'}</dd>
    </>
  );
}

export default CustomerTypeCell;
